import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

export type MockSourceResult = { data: unknown } | { error: FetchBaseQueryError };

function applyParams(url: string, params: unknown): string {
  if (!params || typeof params !== "object" || Array.isArray(params)) return url;

  const parsed = new URL(url, "http://mock.local");
  Object.entries(params as Record<string, unknown>).forEach(function appendParam([key, value]) {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach(function appendEntry(entry) {
        if (entry !== null && entry !== undefined) parsed.searchParams.append(key, String(entry));
      });
      return;
    }
    parsed.searchParams.set(key, String(value));
  });

  return `${parsed.pathname}${parsed.search}`;
}

function readRequest(args: string | FetchArgs): { url: string; method: string; body?: unknown } {
  if (typeof args === "string") return { url: args, method: "GET" };
  return {
    url: applyParams(args.url, "params" in args ? args.params : undefined),
    method: args.method ?? "GET",
    body: args.body,
  };
}

function buildMissingError(url: string): FetchBaseQueryError {
  return { status: 404, data: { detail: "accounting_mock_missing", path: url } };
}

export async function resolveAccountingMock(args: string | FetchArgs): Promise<MockSourceResult> {
  const request = readRequest(args);

  const [{ handleAccountingMockRequest }, { handleAccountingExtraMockRequest }] = await Promise.all([
    import("@/features/accounting/utils/mockHandlers"),
    import("@/shared/api/mockAccountingExtraHandlers"),
  ]);

  const extra = handleAccountingExtraMockRequest(request);
  if (extra) return extra;

  const base = handleAccountingMockRequest(request);
  if (base) return base;

  if (import.meta.env.DEV) {
    console.warn(`[oneC] no accounting mock for ${request.method} ${request.url}`);
  }
  return { error: buildMissingError(request.url) };
}
